import { zodResolver } from "@hookform/resolvers/zod";
import { type DefaultValues, type FieldValues, useForm } from "react-hook-form";
import { toast } from "sonner";
import { getFormValuesDiff } from "web_myplann/Portfolio/PortfolioShared/getFormValuesDiff";
import { useLeaveConfirmation } from "web_myplann/Shared/Hooks/useLeaveConfirmation";
import { useNavigateBackOrDefault } from "web_myplann/Shared/Hooks/useNavigateBackOrDefault";
import type { z } from "zod";

type MutateFn<TPatch, TValues> = (
	data: TPatch,
	options: {
		onSuccess: () => void;
		onError: () => void;
	},
	values: TValues,
) => void;

type DiffOf<TInput, TOutput> = Partial<
	Pick<TOutput, keyof TInput & keyof TOutput>
>;

type Args<TInput extends FieldValues, TOutput> = {
	schema: z.ZodType<TOutput, z.ZodTypeDef & { typeName: string }, TInput>;
	defaultValues: DefaultValues<TInput>;
	mutate: MutateFn<DiffOf<TInput, TOutput>, TOutput>;
	successToast: string;
	redirectTo: string;
	isPending: boolean;
};

export const useEditPortfolioForm = <TInput extends FieldValues, TOutput>({
	schema,
	defaultValues,
	mutate,
	successToast,
	redirectTo,
	isPending,
}: Args<TInput, TOutput>) => {
	const navigateBack = useNavigateBackOrDefault(redirectTo, {
		replaceOnBack: true,
	});

	const form = useForm<TInput, unknown, TOutput>({
		resolver: zodResolver(schema),
		defaultValues,
		mode: "onChange",
	});

	const { dirtyFields, isDirty } = form.formState;
	const { completeSave, cancelSave, createLeaveConfirmation } =
		useLeaveConfirmation({ hasUnsavedChanges: isDirty, isPending });

	const onSubmit = form.handleSubmit((data) => {
		const partial = getFormValuesDiff<TInput, TOutput>(dirtyFields, data);

		mutate(
			partial,
			{
				onSuccess: () => {
					toast.success(successToast);
					completeSave(navigateBack);
				},
				onError: cancelSave,
			},
			data,
		);
	}, cancelSave);

	const leaveConfirmation = createLeaveConfirmation(() => void onSubmit());

	return { form, onSubmit, leaveConfirmation };
};
